import { LockKeyholeIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { PrivateGitSelector } from '@/components/form/private-git-selector'
import { PublicGitInput } from '@/components/form/public-git-input'
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/lib/utils'
import { BitBucketLogo, GitHubLogo, GitLabLogo } from '@/logos'
import { GitLogo } from '@/logos/git'
import type { FC } from 'react'

type Props = {
  hasVerifiedCard?: boolean
  showBranchSelector?: boolean
}

export const StaticSiteSourceSelector: FC<Props> = ({
  hasVerifiedCard,
  showBranchSelector = true,
}) => {
  const form = useFormContext()

  return (
    <FormField
      control={form.control}
      name="source"
      render={({ field: { value, onChange } }) => (
        <>
          <FormItem>
            <FormControl>
              <RadioGroup
                className="grid grid-cols-2 gap-3"
                onValueChange={onChange}
                value={value}
              >
                <Label
                  aria-disabled={!hasVerifiedCard}
                  className={cn(
                    'cursor-pointer relative has-[[data-state=checked]]:border-ring has-[[data-state=checked]]:bg-input/20',
                    'flex flex-col items-center justify-center gap-3 rounded-lg border p-3 py-6',
                    {
                      'cursor-not-allowed': !hasVerifiedCard,
                      'opacity-80': !hasVerifiedCard,
                    }
                  )}
                  key="privateGit"
                >
                  <RadioGroupItem
                    value="privateGit"
                    id="privateGit"
                    disabled={!hasVerifiedCard}
                    className="absolute top-3 left-3 data-[state=checked]:border-primary"
                  />
                  <div className="grid gap-2 text-center place-items-center font-normal">
                    <div className="flex items-center gap-2">
                      <GitHubLogo className="size-4" />
                      <BitBucketLogo className="size-4" />
                      <GitLabLogo className="size-4" />
                    </div>
                    <div className="flex items-center gap-2">
                      {!hasVerifiedCard ? (
                        <LockKeyholeIcon className="size-3.5" />
                      ) : null}
                      <div className="font-medium">Private GIT repository</div>
                    </div>
                  </div>
                </Label>
                <Label
                  aria-disabled={!hasVerifiedCard}
                  className={cn(
                    'cursor-pointer relative has-[[data-state=checked]]:border-ring has-[[data-state=checked]]:bg-input/20',
                    'flex flex-col items-center justify-center gap-3 rounded-lg border p-3 py-6',
                    {
                      'cursor-not-allowed': !hasVerifiedCard,
                      'opacity-80': !hasVerifiedCard,
                    }
                  )}
                  key="publicGit"
                >
                  <RadioGroupItem
                    value="publicGit"
                    id="publicGit"
                    disabled={!hasVerifiedCard}
                    className={cn(
                      'absolute top-3 left-3 data-[state=checked]:border-primary'
                    )}
                  />
                  <div className="grid w-full place-items-center gap-2 text-center font-normal">
                    <GitLogo className="size-4" />
                    <div className="flex items-center gap-2">
                      {!hasVerifiedCard ? (
                        <LockKeyholeIcon className="size-3.5" />
                      ) : null}
                      <div className="font-medium">Public GIT repository</div>
                    </div>
                  </div>
                </Label>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
          {value === 'privateGit' ? (
            <PrivateGitSelector showBranchSelector={showBranchSelector} />
          ) : null}
          {value === 'publicGit' ? (
            <PublicGitInput showBranchSelector={showBranchSelector} />
          ) : null}
        </>
      )}
    />
  )
}
